import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Bot, RefreshCw, TrendingUp, TrendingDown, Clock, Radio, Activity, CheckCircle2, Settings2, Save, Zap, Power } from "lucide-react";

const STATUS_META = {
  BEKLIYOR: { label: "🟡 BEKLİYOR", color: "#F59E0B" },
  ENTRY1: { label: "🔵 ENTRY 1", color: "#3B82F6" },
  ENTRY2: { label: "🔵 ENTRY 2", color: "#3B82F6" },
  ENTRY3: { label: "🔵 ENTRY 3", color: "#3B82F6" },
  TP1: { label: "🟢 TP1 GELDİ", color: "#10B981" },
  TP2: { label: "🟢 TP2 GELDİ", color: "#10B981" },
  TP3: { label: "🏆 TP3 GELDİ", color: "#8B5CF6" },
  SL: { label: "🔴 SL GELDİ", color: "#EF4444" },
  MANUEL: { label: "⚪ MANUEL KAPATILDI", color: "#9CA3AF" },
};

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

const Cell = ({ label, value, color, sub }) => (
  <div className="text-center">
    <div className="text-[9px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</div>
    <div className="font-mono-term text-xs font-semibold" style={{ color: color || "var(--text-primary)" }}>{value ?? "—"}</div>
    {sub && <div className="text-[8px] font-mono-term" style={{ color: "var(--text-muted)" }}>{sub}</div>}
  </div>
);

function AutoCard({ trade }) {
  const isLong = trade.direction === "LONG";
  const dirColor = isLong ? "var(--long)" : "var(--short)";
  const meta = STATUS_META[trade.status] || STATUS_META.BEKLIYOR;
  const s = trade.setup || {};
  return (
    <div data-testid="auto-signal-card" className="rounded-xl border p-4" style={{ background: "var(--bg-main)", borderColor: "var(--border-subtle)" }}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          {isLong ? <TrendingUp className="w-4 h-4" style={{ color: dirColor }} /> : <TrendingDown className="w-4 h-4" style={{ color: dirColor }} />}
          <span className="font-bold text-sm">{trade.coin}</span>
          <span className="text-[10px] font-mono-term px-1.5 py-0.5 rounded" style={{ background: "rgba(59,130,246,0.15)", color: "#93C5FD" }}>{trade.timeframe}</span>
          <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ color: dirColor, background: isLong ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)" }}>{trade.direction}</span>
        </div>
        <span className="text-xs font-semibold" style={{ color: meta.color }}>{meta.label}</span>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-2">
        <Cell label="Giriş 1" value="Market" color="var(--entry)" />
        <Cell label="Giriş 2" value={s.entry2} color="var(--entry)" />
        <Cell label="Giriş 3" value={s.entry3} color="var(--entry)" />
        {s.sl ? <Cell label="SL" value={s.sl} color="var(--short)" /> : null}
        <Cell label="TP1" value={s.tp1} color="var(--long)" sub={s.tp1_prob != null ? `%${s.tp1_prob}` : "%100 çıkış"} />
        {s.tp2 ? <Cell label="TP2" value={s.tp2} color="var(--long)" sub={s.tp2_prob != null ? `%${s.tp2_prob}` : null} /> : null}
        {s.tp3 ? <Cell label="TP3" value={s.tp3} color="var(--long)" sub={s.tp3_prob != null ? `%${s.tp3_prob}` : null} /> : null}
        <Cell label="R/R" value={s.rr} color="var(--rr)" />
      </div>

      {(trade.rsi_prev != null) && (
        <div className="text-[11px] mb-2 font-mono-term" style={{ color: "var(--text-secondary)" }}>
          RSI {trade.timeframe}: {trade.rsi_prev} → {trade.rsi_last}
        </div>
      )}

      {trade.realized_pnl != null && (
        <div data-testid="auto-signal-pnl" className="flex items-center justify-between text-xs mb-2 px-2 py-1.5 rounded-lg"
             style={{ background: trade.realized_pnl >= 0 ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)" }}>
          <span style={{ color: "var(--text-muted)" }}>Gerçekleşen K/Z</span>
          <span className="font-mono-term font-bold" style={{ color: trade.realized_pnl >= 0 ? "var(--long)" : "var(--short)" }}>
            {trade.realized_pnl >= 0 ? "+" : ""}{Number(trade.realized_pnl).toFixed(2)} USDT
          </span>
        </div>
      )}

      <div className="flex items-center justify-between text-[11px]" style={{ color: "var(--text-muted)" }}>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(trade.created_at)}</span>
        {trade.last_price != null && (
          <span className="flex items-center gap-1 font-mono-term" style={{ color: "var(--entry)" }}>
            <Radio className="w-3 h-3 pulse-glow" /> {trade.last_price}
          </span>
        )}
      </div>
    </div>
  );
}

export default function AutoSignalsPanel({ signals = [], onScan, scanning }) {
  const [tab, setTab] = useState("active");
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bingx, setBingx] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/bingx/status").then(({ data }) => setBingx(data)).catch(() => {});
  }, []);

  const setField = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const saveSettings = async () => {
    setSaving(true);
    try {
      const payload = {
        rsi_upper: Number(settings.rsi_upper),
        rsi_lower: Number(settings.rsi_lower),
        rsi_period: Number(settings.rsi_period),
        top_n: Number(settings.top_n),
        auto_trade_enabled: !!settings.auto_trade_enabled,
        margin_pct: Number(settings.margin_pct),
        leverage: Number(settings.leverage),
      };
      const { data } = await api.put("/settings", payload);
      setSettings(data);
      toast.success("Ayarlar kaydedildi");
      setShowSettings(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const toggleAutoTrade = async () => {
    if (!settings) return;
    const next = !settings.auto_trade_enabled;
    try {
      const { data } = await api.put("/settings", {
        rsi_upper: Number(settings.rsi_upper),
        rsi_lower: Number(settings.rsi_lower),
        rsi_period: Number(settings.rsi_period),
        top_n: Number(settings.top_n),
        auto_trade_enabled: next,
        margin_pct: Number(settings.margin_pct),
        leverage: Number(settings.leverage),
      });
      setSettings(data);
      toast.success(next ? "Oto-işlem AÇILDI" : "Oto-işlem PASİFE alındı");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Değiştirilemedi");
    }
  };

  const active = signals.filter((t) => ["BEKLIYOR", "ENTRY1", "ENTRY2", "ENTRY3", "ENTRY4"].includes(t.status));
  const completed = signals.filter((t) => ["TP1", "TP2", "TP3", "SL", "MANUEL"].includes(t.status));
  const list = tab === "active" ? active : completed;

  const settingFields = [
    { k: "rsi_upper", label: "RSI Üst Eşik (SHORT)", hint: "örn. 80 · 50-100" },
    { k: "rsi_lower", label: "RSI Alt Eşik (LONG)", hint: "örn. 20 · 0-50" },
    { k: "rsi_period", label: "RSI Periyodu", hint: "2-50" },
    { k: "top_n", label: "Taranacak Coin", hint: "5-200" },
    { k: "margin_pct", label: "Teminat % (giriş başına)", hint: "0.1-50" },
    { k: "leverage", label: "Kaldıraç", hint: "1-125" },
  ];

  return (
    <div data-testid="auto-signals-panel" className="rounded-2xl border" style={{ background: "var(--bg-card)", borderColor: "var(--border-subtle)" }}>
      <div className="px-5 sm:px-6 py-4 border-b flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-blue-400" />
          <div>
            <h3 className="text-sm font-bold">Oto Sinyal <span className="text-blue-400">(RSI)</span></h3>
            <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>
              RSI taraması · RSI&gt;{settings?.rsi_upper ?? 80} SHORT · RSI&lt;{settings?.rsi_lower ?? 20} LONG · {settings?.top_n ?? 200} coin · 1m/5m · her 15 dk
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="auto-trade-power-button"
            onClick={toggleAutoTrade}
            className="inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-xl border transition-colors hover:brightness-125"
            style={{
              borderColor: settings?.auto_trade_enabled ? "rgba(16,185,129,0.5)" : "rgba(239,68,68,0.5)",
              color: settings?.auto_trade_enabled ? "#10B981" : "#EF4444",
              background: settings?.auto_trade_enabled ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)",
            }}
          >
            <Power className="w-4 h-4" />
            {settings?.auto_trade_enabled ? "Oto-İşlem: AÇIK" : "Oto-İşlem: KAPALI"}
          </button>
          <button
            data-testid="toggle-settings-button"
            onClick={() => setShowSettings((v) => !v)}
            className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-xl border transition-colors hover:brightness-125"
            style={{ borderColor: "var(--border-subtle)", color: showSettings ? "#A78BFA" : "var(--text-secondary)" }}
          >
            <Settings2 className="w-4 h-4" /> Ayarlar
          </button>
          <button
            data-testid="scan-now-button"
            onClick={onScan}
            disabled={scanning}
            className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-xl border transition-colors hover:brightness-125 disabled:opacity-50"
            style={{ borderColor: "rgba(59,130,246,0.35)", color: "#60A5FA" }}
          >
            <RefreshCw className={`w-4 h-4 ${scanning ? "animate-spin" : ""}`} /> Şimdi Tara
          </button>
        </div>
      </div>

      {showSettings && settings && (
        <div data-testid="rsi-settings-form" className="px-5 sm:px-6 py-4 border-b" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-main)" }}>
          <div className="mb-4 rounded-xl border p-3" style={{ borderColor: settings.auto_trade_enabled ? "rgba(16,185,129,0.4)" : "var(--border-subtle)", background: "var(--bg-card)" }}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-sm font-bold flex items-center gap-2">
                  <Zap className="w-4 h-4 text-emerald-400" /> Otomatik Borsa İşlemi (BingX)
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                  {bingx?.configured
                    ? <>Bağlı · {bingx.env === "prod-live" ? "CANLI" : "DEMO"}{bingx.balance != null ? ` · Bakiye: ${bingx.balance} USDT` : ""}{bingx.error ? ` · ${bingx.error}` : ""}</>
                    : "API anahtarları eklenmedi (.env). Anahtar olmadan açılamaz."}
                </div>
              </div>
              <button
                data-testid="auto-trade-toggle"
                onClick={() => setField("auto_trade_enabled", !settings.auto_trade_enabled)}
                disabled={!bingx?.configured}
                className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-40"
                style={{ background: settings.auto_trade_enabled ? "#10B981" : "var(--border-subtle)" }}
              >
                <span className="inline-block h-5 w-5 transform rounded-full bg-white transition-transform" style={{ transform: settings.auto_trade_enabled ? "translateX(22px)" : "translateX(4px)" }} />
              </button>
            </div>
            <div className="text-[10px] mt-2" style={{ color: "var(--text-muted)" }}>
              Açıkken: her oto sinyalde Giriş 1'de pozisyon açılır + TP1 kurulur (TP2/TP3 ve SL konmaz). Giriş 2/3 tetiklenince aynı miktar eklenir.
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {settingFields.map((f) => (
              <div key={f.k}>
                <label className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>{f.label}</label>
                <input
                  data-testid={`setting-${f.k}`}
                  type="number"
                  value={settings[f.k]}
                  onChange={(e) => setField(f.k, e.target.value)}
                  className="w-full rounded-lg px-3 py-2 text-sm font-mono-term outline-none border focus:border-blue-500"
                  style={{ background: "var(--bg-card)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }}
                />
                <span className="text-[9px]" style={{ color: "var(--text-muted)" }}>{f.hint}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <button
              data-testid="save-settings-button"
              onClick={saveSettings}
              disabled={saving}
              className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-xl text-white transition-all hover:brightness-110 disabled:opacity-60"
              style={{ background: "#8B5CF6", boxShadow: "0 0 20px rgba(139,92,246,0.3)" }}
            >
              <Save className="w-4 h-4" /> {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </div>
      )}

      <div className="px-5 sm:px-6 pt-3 flex items-center gap-2">
        <button data-testid="auto-tab-active" onClick={() => setTab("active")}
          className="inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1.5 rounded-lg transition-colors"
          style={{ background: tab === "active" ? "rgba(16,185,129,0.12)" : "transparent", color: tab === "active" ? "var(--long)" : "var(--text-muted)" }}>
          <Activity className="w-4 h-4" /> Aktif <span className="font-mono-term">({active.length})</span>
        </button>
        <button data-testid="auto-tab-completed" onClick={() => setTab("completed")}
          className="inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1.5 rounded-lg transition-colors"
          style={{ background: tab === "completed" ? "rgba(156,163,175,0.12)" : "transparent", color: tab === "completed" ? "var(--text-primary)" : "var(--text-muted)" }}>
          <CheckCircle2 className="w-4 h-4" /> Tamamlanan <span className="font-mono-term">({completed.length})</span>
        </button>
      </div>

      <div className="p-4 sm:p-5 space-y-3">
        {list.length === 0 ? (
          <div className="py-10 text-center text-sm flex flex-col items-center gap-2" style={{ color: "var(--text-muted)" }}>
            <Bot className="w-6 h-6" />
            {tab === "active" ? "Henüz oto sinyal yok. Tarama her 5 dakikada otomatik çalışır." : "Tamamlanan oto sinyal yok"}
          </div>
        ) : (
          list.map((t) => <AutoCard key={t.id} trade={t} />)
        )}
      </div>
    </div>
  );
}
